import React, { useEffect, useMemo, useState } from "react";
import { Users, Download, Mail, TrendingUp, ShieldCheck, Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard, Loading } from "@/components/Primitives";
import DataTable from "@/components/DataTable";

const CAT_COLORS = {
  low: "#059669", borderline: "#C9A24B", intermediate: "#F59E0B", high: "#DC2626",
};

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCSV(rows) {
  const headers = ["timestamp", "email", "source", "risk_pct", "category", "age_band", "sex", "consent_marketing", "ip"];
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push(headers.map((h) => csvEscape(r[h])).join(","));
  }
  return lines.join("\n");
}

function downloadCSV(rows, filename) {
  const csv = toCSV(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/public/leads", { params: { limit: 1000 } })
      .then((r) => setLeads(r.data || []))
      .catch((err) => {
        const msg = err.response?.status === 403
          ? "Admin role required to view leads."
          : (err.response?.data?.detail || "Unable to load leads");
        setError(msg);
      });
  }, []);

  const kpis = useMemo(() => {
    if (!leads) return null;
    const total = leads.length;
    const consented = leads.filter((l) => l.consent_marketing).length;
    const sevenDayCutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const last7 = leads.filter((l) => l.timestamp && new Date(l.timestamp).getTime() >= sevenDayCutoff).length;
    const highRisk = leads.filter((l) => ["intermediate", "high"].includes(l.category)).length;
    return { total, consented, last7, highRisk };
  }, [leads]);

  const exportCSV = () => {
    if (!leads?.length) { toast.error("No leads to export"); return; }
    const ts = new Date().toISOString().slice(0, 10);
    downloadCSV(leads, `zidamd-leads-${ts}.csv`);
    toast.success(`Exported ${leads.length} lead${leads.length !== 1 ? "s" : ""}`);
  };

  const columns = [
    {
      key: "timestamp", label: "Captured",
      render: (r) => r.timestamp
        ? <span className="text-xs tabular-nums">{new Date(r.timestamp).toLocaleString()}</span>
        : "—",
    },
    {
      key: "email", label: "Email",
      render: (r) => (
        <a href={`mailto:${r.email}`} className="text-brand-primary hover:underline text-sm" data-testid={`lead-email-${r.id}`}>
          {r.email}
        </a>
      ),
    },
    {
      key: "risk_pct", label: "Risk %",
      render: (r) => r.risk_pct != null
        ? <span className="tabular-nums font-semibold" style={{ color: CAT_COLORS[r.category] || "inherit" }}>{r.risk_pct}%</span>
        : "—",
    },
    {
      key: "category", label: "Band",
      render: (r) => r.category
        ? <span className="px-2 py-0.5 rounded-full text-[10px] uppercase font-bold border"
            style={{ background: `${CAT_COLORS[r.category]}15`, color: CAT_COLORS[r.category], borderColor: `${CAT_COLORS[r.category]}55` }}>
            {r.category}
          </span>
        : "—",
    },
    { key: "age_band", label: "Age" },
    { key: "sex", label: "Sex" },
    {
      key: "consent_marketing", label: "Consent",
      render: (r) => r.consent_marketing
        ? <span className="text-emerald-700 text-xs font-semibold">Yes</span>
        : <span className="text-slate-400 text-xs">No</span>,
    },
    { key: "source", label: "Source", render: (r) => <span className="text-xs text-brand-muted">{r.source}</span> },
  ];

  if (error) {
    return (
      <div data-testid="admin-leads-error" className="p-6">
        <PageHeader eyebrow="Admin" title="Public Leads" subtitle="Heart-Risk widget lead capture" />
        <SectionCard>
          <div className="py-10 text-center text-sm text-red-700">{error}</div>
        </SectionCard>
      </div>
    );
  }

  if (!leads) return <Loading label="Loading leads…" />;

  return (
    <div data-testid="admin-leads-page">
      <PageHeader
        eyebrow="Admin"
        title="Public Leads"
        subtitle="De-identified lead capture from the public Heart-Risk widget. No PHI stored — only email, age decade, sex, and risk band."
        actions={
          <button
            onClick={exportCSV}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-brand-primary text-white hover:opacity-90 disabled:opacity-40"
            style={{ minHeight: 44 }}
            disabled={!leads.length}
            data-testid="leads-export-csv">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        }
        testid="admin-leads-header"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard icon={Users} label="Total leads" value={kpis.total} testid="kpi-total" />
        <KpiCard icon={TrendingUp} label="Last 7 days" value={kpis.last7} testid="kpi-last7" />
        <KpiCard icon={Heart} label="Intermediate / High risk" value={kpis.highRisk} testid="kpi-highrisk" />
        <KpiCard icon={ShieldCheck} label="Marketing consent" value={kpis.consented} testid="kpi-consented" accent />
      </div>

      <SectionCard
        title="All leads"
        subtitle={`${leads.length} record${leads.length !== 1 ? "s" : ""} · sortable, filterable`}
        testid="leads-table-card"
      >
        <DataTable
          data={leads}
          columns={columns}
          searchKeys={["email", "category", "source", "age_band", "sex"]}
          testid="leads-table"
          emptyMessage="No leads captured yet. Share /heart-risk to start collecting."
        />
      </SectionCard>

      <div className="mt-6 flex items-start gap-2 text-xs text-brand-muted bg-brand-bg rounded-md p-3" data-testid="leads-privacy-note">
        <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          Privacy note: only de-identified data is stored. Ages are bucketed to the decade (e.g. 50s), no full date of birth, no clinical history. Leads are captured server-side at <code className="bg-white px-1 rounded">POST /api/public/leads</code>.
        </div>
      </div>
    </div>
  );
}
